import React from 'react'
import { FormatDateOptions, useIntl, defineMessages } from 'react-intl'
import { twMerge } from 'tailwind-merge'
import { CustomLinkComponent } from 'msp-components'
import { useAccount } from 'msp-integrations'

import OrderStatus from '@/components/atoms/OrderStatus/OrderStatus'
import OrderDeliveryDate from '@/components/atoms/OrderDeliveryDate/OrderDeliveryDate'
import { MAX_IMAGES_TO_DISPLAY } from '@/constants'

import * as S from './AccountOrderHistoryItem.styles'
import * as T from './AccountOrderHistoryItem.types'

const translations = defineMessages({
  totalPrice: {
    defaultMessage: 'Total price: {price}',
    id: 'msp.account.order_history.order_details.total_price'
  },
  viewOrder: {
    defaultMessage: 'View Order Details',
    id: 'msp.account.order_history.order_details.view'
  }
})

const AccountOrderHistoryItem = ({
  order,
  className = '',
  maxProductImagesToDisplay = MAX_IMAGES_TO_DISPLAY
}: T.AccountOrderHistoryItemProps) => {
  const { formatMessage, formatDate, formatNumber } = useIntl()
  const {
    orders: { productFallbackImage, convertToOrderModel }
  } = useAccount()
  const orderModel = convertToOrderModel(order)
  const dateFormattingOpts = {
    year: 'numeric',
    day: '2-digit',
    month: '2-digit'
  } as FormatDateOptions

  const productImages = orderModel.getProductImages()
  const productQuantity = orderModel?.productItems.length || 0
  const hasTooManyProducts = productQuantity > maxProductImagesToDisplay

  if (hasTooManyProducts) {
    productImages.splice(maxProductImagesToDisplay)
  }

  return (
    <CustomLinkComponent
      key={`order-${orderModel.orderNo}`}
      href={`/account/orders/${orderModel.orderNo}`}
      className={twMerge(S.MainWrapper, className)}
      uppercase={false}
      inline
    >
      <div className={S.OrderContentWrapper}>
        <div className={S.OrderLeftColWrapper}>
          <div className={S.OrderDateInfoWrapper}>
            <div className={S.OrderDateContent}>
              {formatDate(
                new Date(orderModel.creationDate),
                dateFormattingOpts
              )}
            </div>
            <div className={S.OrderNoContent}>#{orderModel.orderNo}</div>
          </div>
          <OrderStatus order={orderModel} className={S.OrderStatus} />
          <OrderDeliveryDate order={orderModel} />
          <div className={S.OrderTotalPrice}>
            {formatMessage(translations.totalPrice, {
              price: formatNumber(orderModel.orderTotal, {
                style: 'currency',
                currency: orderModel.currency,
                currencyDisplay: 'code'
              })
            })}
          </div>
        </div>
        <div className={S.OrderRightColWrapper}>
          {productImages.length > 0 &&
            productImages.map((image, index) => (
              <div
                key={`product-img-${image.productId}`}
                className={S.OrderProductsImageWrapper}
              >
                <img
                  alt={image.alt}
                  src={image.src || productFallbackImage}
                  className={S.OrderProductImage}
                />
                {hasTooManyProducts &&
                  index + 1 === maxProductImagesToDisplay && (
                    <div className={S.OrderProductImageOverlayInfo}>
                      +{1 + productQuantity - maxProductImagesToDisplay}
                    </div>
                  )}
              </div>
            ))}
        </div>
      </div>
      <div className={S.OrderActionsWrapper}>
        {formatMessage(translations.viewOrder)}
      </div>
    </CustomLinkComponent>
  )
}

export default AccountOrderHistoryItem
